import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Eye, EyeOff, Loader2, AlertCircle, ArrowRight, Fingerprint } from 'lucide-react';
import axios from 'axios';
import { isNative } from '../lib/platform';
import { isBiometricEnabled, isBiometricAvailable, unlockWithBiometric } from '../lib/biometric';

const API = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const { user, login, register, loginWithGoogle } = useApp();

  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [error, setError] = useState('');
  const [biometricReady, setBiometricReady] = useState(false);
  const [biometricLabel, setBiometricLabel] = useState('Biometrics');

  // Native shell: if the user has opted-in to biometric login, show the
  // "Unlock" button up top so they can skip typing their password.
  useEffect(() => {
    if (!isNative()) return;
    (async () => {
      const [avail, opted] = await Promise.all([
        isBiometricAvailable(),
        isBiometricEnabled(),
      ]);
      if (avail.available && opted) {
        setBiometricReady(true);
        if (avail.face) setBiometricLabel('Face ID');
        else if (avail.fingerprint) setBiometricLabel('Fingerprint');
      }
    })();
  }, []);

  // Native OAuth bridge: when opened as /login?mobile_auth=1&provider=google
  // from the Expo in-app browser, hand the session back to the app.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('mobile_auth') !== '1') return;
      sessionStorage.setItem('mobile_auth', '1');
      // Already authenticated in this browser (shared cookies)? Skip OAuth and
      // hand the EXISTING session straight back to the app — otherwise the page
      // would just bounce to /dashboard and never return the token.
      const existingToken = localStorage.getItem('connectify_session_token');
      if (existingToken) {
        const existingUser = localStorage.getItem('connectify_user') || '{}';
        window.location.replace(
          `mobile://auth?token=${encodeURIComponent(existingToken)}&user=${encodeURIComponent(existingUser)}`
        );
        return;
      }
      const provider = params.get('provider');
      if (provider === 'google') setTimeout(() => handleGoogleLogin(), 50);
      else if (provider === 'apple') setTimeout(() => handleAppleLogin(), 50);
    } catch (_) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Surface OAuth failures. Apple/Google callbacks redirect to
  // /login?error=<reason>&detail=<msg> on failure; without this the user
  // silently lands back on the login page with no explanation.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const err = params.get('error');
      if (!err) return;
      const detail = params.get('detail') || params.get('error_description') || '';
      const friendly = {
        apple_auth_failed: 'Apple sign-in was cancelled or failed. Please try again.',
        auth_failed: 'Apple sign-in could not be completed. Please try again.',
        token_verification_failed: "We couldn't verify your Apple sign-in. Please try again.",
        token_exchange_failed: 'Apple sign-in could not be completed (token exchange failed).',
        no_user_info: "Apple didn't share your account details. Please try again and allow email sharing.",
        no_auth_code: 'Apple sign-in was interrupted. Please try again.',
        no_user_info_apple: "Apple didn't share your account details. Please try again.",
      }[err] || 'Sign-in failed. Please try again.';
      setError(detail ? `${friendly} (${err}: ${String(detail).slice(0, 160)})` : friendly);
      // Clean the query so a refresh doesn't keep showing the error.
      window.history.replaceState({}, '', window.location.pathname);
    } catch (_) { /* ignore */ }
  }, []);


  const handleBiometricUnlock = async () => {
    setError('');
    const r = await unlockWithBiometric();
    if (!r.ok) {
      if (r.reason && r.reason.includes('cancel')) return;
      setError('Biometric unlock failed. Please sign in with your password.');
      return;
    }
    // Restore session via the OAuth path — same shape (email + token, no
    // password needed). We pull the last-saved user profile from storage;
    // if that's missing, we call /user/session to refresh.
    try {
      let userData = null;
      const cached = localStorage.getItem('connectify_user');
      if (cached) userData = JSON.parse(cached);
      if (!userData) {
        const resp = await axios.get(`${API}/api/user/session`, {
          headers: { Authorization: `Bearer ${r.sessionToken}` },
        });
        userData = resp.data.user;
      }
      const ok = await loginWithGoogle(userData, r.sessionToken);
      if (ok) navigate('/dashboard');
    } catch (e) {
      setError('Your session has expired. Please sign in with your password.');
    }
  };

  // Redirect if already logged in — but during a native (mobile_auth) session,
  // hand the token back to the app via the deep link instead of going to /dashboard.
  useEffect(() => {
    if (!user) return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('mobile_auth') === '1') {
        const existingToken = localStorage.getItem('connectify_session_token');
        if (existingToken) {
          const existingUser = localStorage.getItem('connectify_user') || '{}';
          sessionStorage.removeItem('mobile_auth');
          window.location.replace(
            `mobile://auth?token=${encodeURIComponent(existingToken)}&user=${encodeURIComponent(existingUser)}`
          );
          return;
        }
      }
    } catch (_) { /* ignore */ }
    navigate('/dashboard');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      let success = false;
      if (mode === 'login') {
        success = await login(email, password);
        if (!success) {
          setError('Invalid email or password');
        }
      } else {
        if (!firstName || !lastName) {
          setError('Please fill in all fields');
          setIsLoading(false);
          return;
        }
        success = await register({ email, password, firstName, lastName });
        if (!success) {
          setError('Registration failed. Email may already be in use.');
        }
      }

      if (success) {
        navigate('/dashboard');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Native (Expo) app opens /login?mobile_auth=1 inside an in-app browser.
  // Persist the flag so AuthCallback can hand the session back via deep link.
  const persistMobileAuthFlag = () => {
    try {
      if (new URLSearchParams(window.location.search).get('mobile_auth') === '1') {
        sessionStorage.setItem('mobile_auth', '1');
      }
    } catch (_) { /* ignore */ }
  };

  const handleGoogleLogin = async () => {
    persistMobileAuthFlag();
    setIsGoogleLoading(true);
    setError('');
    try {
      // Get the Google OAuth URL from the backend
      const response = await axios.get(`${API}/api/auth/google/url`);
      const { url } = response.data;
      
      // Store the current path to redirect back after auth
      sessionStorage.setItem('authRedirect', '/dashboard');
      
      // Redirect to Google OAuth
      window.location.href = url;
    } catch (err) {
      setError('Failed to initiate Google login. Please try again.');
      setIsGoogleLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    persistMobileAuthFlag();
    setIsAppleLoading(true);
    setError('');
    try {
      // Get the Apple OAuth URL from the backend, passing our origin explicitly
      const currentOrigin = window.location.origin;
      const response = await axios.get(`${API}/api/auth/apple/url?frontend_origin=${encodeURIComponent(currentOrigin)}`);
      const { url } = response.data;
      
      // Store the current path to redirect back after auth
      sessionStorage.setItem('authRedirect', '/dashboard');
      
      // Redirect to Apple OAuth
      window.location.href = url;
    } catch (err) {
      setError('Failed to initiate Apple login. Please try again.');
      setIsAppleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      <Navbar />

      <div className="pt-32 pb-20 px-4">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-black text-gray-900 dark:text-neutral-100">
              {mode === 'login' ? 'Welcome Back' : 'Create Account'}
            </h1>
            <p className="text-gray-600 dark:text-neutral-400 mt-2">
              {mode === 'login' 
                ? 'Sign in to access your eSIM plans' 
                : 'Join millions of connected travelers'}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white dark:bg-neutral-900 rounded-3xl p-8 shadow-sm border border-gray-100 dark:border-neutral-800">
            {/* Social Login Buttons */}
            <div className="space-y-3 mb-6">
              {biometricReady && mode === 'login' && (
                <button
                  onClick={handleBiometricUnlock}
                  data-testid="biometric-unlock-btn"
                  className="w-full py-3.5 px-4 bg-black text-white rounded-xl font-semibold hover:bg-gray-800 transition-all flex items-center justify-center gap-3 dark:border-2 dark:border-white"
                >
                  <Fingerprint className="w-5 h-5" />
                  Unlock with {biometricLabel}
                </button>
              )}
              {/* Google Sign In Button */}
              <button
                onClick={handleGoogleLogin}
                disabled={isGoogleLoading || isAppleLoading}
                className="w-full py-3.5 px-4 bg-white dark:bg-neutral-900 border-2 border-gray-200 dark:border-neutral-800 rounded-xl font-semibold text-gray-700 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800 hover:border-gray-300 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                data-testid="google-signin-btn"
              >
                {isGoogleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </>
                )}
              </button>

              {/* Apple Sign In Button */}
              <button
                onClick={handleAppleLogin}
                disabled={isGoogleLoading || isAppleLoading}
                className="w-full py-3.5 px-4 bg-black border-2 border-black dark:border-neutral-300 rounded-xl font-semibold text-white hover:bg-gray-900 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                data-testid="apple-signin-btn"
              >
                {isAppleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    Continue with Apple
                  </>
                )}
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-gray-200 dark:bg-neutral-700"></div>
              <span className="text-sm text-gray-500 dark:text-neutral-500">or continue with email</span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-neutral-700"></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === 'register' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-300 mb-2">First Name</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black transition-colors"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-300 mb-2">Last Name</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black transition-colors"
                      placeholder="Doe"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-300 mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black transition-colors"
                  placeholder="you@example.com"
                  data-testid="login-email"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-300">Password</label>
                  {mode === 'login' && (
                    <Link to="/forgot-password" className="text-sm text-gray-600 dark:text-neutral-400 hover:text-black dark:hover:text-white">
                      Forgot password?
                    </Link>
                  )}
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black transition-colors pr-12"
                    placeholder="••••••••"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-500/10 rounded-xl">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-black text-white rounded-xl font-bold text-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="login-submit"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {mode === 'login' ? 'Sign In' : 'Create Account'}
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
                className="text-gray-600 dark:text-neutral-400 hover:text-black dark:hover:text-white transition-colors"
              >
                {mode === 'login' 
                  ? "Don't have an account? Sign up" 
                  : 'Already have an account? Sign in'}
              </button>
            </div>
          </div>

          {/* Bottom Info */}
          <p className="text-center text-xs text-gray-500 dark:text-neutral-500 mt-6">
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Login;
